import React from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import {
  CHOICE_DRIVER_TYPE,
  ROBOT_DRIVER_TYPE,
} from "../../consts/driverTypes";
import { palette } from "../../theme/palette";

import { driverProfileLockSelector } from "../../reducers/driverManagement/selectors";

import { Text } from "../Base";
import { View } from "../View";
import { LockIcon } from "../Icons";
import RobotDriverMarker from "../RobotDriverMarker";
import ChoiceDriverMarker from "../ChoiceDriverMarker";

const useStyles = makeStyles(() => ({
  container: {
    flex: 1,
    display: "flex",
    gap: "0.5rem",
  },
  nameContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    height: "1.2rem",
    borderRadius: "0.5rem",
    padding: ({ hasLockIcon }) => {
      if (hasLockIcon) return "0rem 0.5rem";
      else return "0rem";
    },
    backgroundColor: ({ driverProfileLock }) => {
      if (driverProfileLock.changed) return palette.lockOrange;
      else if (
        driverProfileLock.lockedByYou &&
        !driverProfileLock.lockedInDifferentBrowser
      )
        return palette.lockGreen;
      else if (driverProfileLock.lockedByUserCode) return palette.lockRed;
      else return palette.transparent;
    },
  },
  name: {
    height: "1.25rem",
    color: "white",
    fontSize: "0.875rem",
    lineHeight: "1.25rem",
    marginTop: ({ hasLockIcon }) => (hasLockIcon ? "0.125rem" : "0rem"),
  },
  lockIcon: {
    color: ({ hasLockIcon }) => {
      if (hasLockIcon) return palette.typography.black.solid;
      else return palette.transparent;
    },
    width: "10px",
    height: "12px",
    marginRight: "0.35rem",
  },
}));

export const DriverName = ({ driver }) => {
  const driverProfileLock = useSelector(
    driverProfileLockSelector(driver.driverCode)
  );
  const hasLockIcon =
    driverProfileLock.changed || driverProfileLock.lockedByUserCode;

  const styles = useStyles({
    hasLockIcon,
    driverProfileLock,
  });

  const getDriverLockTooltipText = () => {
    if (driverProfileLock.changed)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile has been changed.`;
    else if (driverProfileLock.lockedInDifferentBrowser)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile is locked by you in different browser/application.`;
    else if (driverProfileLock.lockedByYou)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile is locked by you.`;
    else if (driverProfileLock.lockedByUserCode)
      return `Driver ${driver.driverCode.split("*")[1]} ${
        driver.driverName
      } profile is locked by ${driverProfileLock.lockedByUserCode} ${
        driverProfileLock.lockedByUserName
      }.`;
    else return ``;
  };

  return (
    <View className={styles.container}>
      <Tooltip
        title={getDriverLockTooltipText()}
        enterDelay={500}
        enterNextDelay={500}
      >
        <View className={styles.nameContainer}>
          {hasLockIcon && <LockIcon className={styles.lockIcon} />}
          <Text className={styles.name} data-testid="driver-name">
            {driver.driverName}
          </Text>
        </View>
      </Tooltip>
      {driver.driverType === CHOICE_DRIVER_TYPE && <ChoiceDriverMarker />}
      {driver.driverType === ROBOT_DRIVER_TYPE && <RobotDriverMarker />}
    </View>
  );
};

export default DriverName;
